/* eslint-disable react/jsx-no-useless-fragment */
import React from 'react';
import { useAuthContext } from '../context';
import { useRouter } from 'next/router';
import { Redirect } from './redirect';
import { useIsEmployerAnAdmin, useEmployerUserInformationLoadingState } from '../hook';
import { consumeLoginTokenFromUrl } from '../utils/login-token-url';
import { internalPath, resolveAndReportLoginDestination } from '../utils/login-destination';
import { isValidToken } from '../utils/jwt';
import { AuthGuardRedirectReason, trackAuthGuardRedirect, trackSessionRecovery } from '../utils/track-auth-guard';
import { readStoredToken } from '../constants/jwt-local-storage';

export type AuthGuardProps = {
  children: React.ReactNode;
  loader: React.ReactNode;
};

export const COLLABORATOR_ROLE_KEYS = {
  ADMIN: 'admin_employer_user',
  BASE: 'base_employer_user',
} as const;

const privatePaths = [
  '/worker/',
  '/employer/',
  '/referral/',
  '/impostazioni',
  '/profilo',
  '/candidature',
  '/porta-un-locale',
  '/v2/employer/',
];
const redirectLocation = '/login';

const isPrivatePath = (path: string) => privatePaths.some((privatePath) => path.startsWith(privatePath));

/**
 * One re-check of the session, for the one state that must not be answered with `/login`: the
 * auth state holds no token while storage still holds a valid one.
 *
 * **Why this exists (login bounce, 30/09/2026).** 14 of 658 interview sessions in a week hopped
 * from `/candidature/:id/intervista` to `/login` a fraction of a second after the worker sent a
 * message, 12 of them on Chrome Android. The backend kept answering 200 for the same user in the
 * same second, and the next document, loaded with the very same stored token, was signed in
 * again. The state that produces it is known: `initialize()` answers a failed `/me` that is not a
 * 401 (a request that never reached the server, a timeout) with `isReady` and no token, and keeps
 * the token in storage on purpose (`use-auth-fn.ts`). What calls `initialize()` at that moment on
 * the interview page was not found; `trackAuthGuardRedirect` is there to say it from production.
 *
 * Whatever the caller, sending a worker holding a valid credential to the login screen in the
 * middle of an interview is the wrong answer, and one that sometimes cannot be undone: two of the
 * fourteen reopened the magic link, which is single use, and stopped there. So the guard asks
 * `/me` once more instead. If it answers, the page carries on; if it does not, the guard falls back
 * on the redirect it always made. Once per stored token until a re-check succeeds, so a network
 * that stays down costs one extra request and never an endless spinner.
 */
/**
 * How long the page waits on the re-check before giving up and redirecting as before. The shared
 * axios client has no timeout of its own, so without this a `/me` that never answers would turn
 * the old immediate redirect into a spinner with no end (Codex review of this change).
 */
export const SESSION_RECOVERY_TIMEOUT_MS = 8000;

const useSessionRecovery = (authContext: ReturnType<typeof useAuthContext>, path: string) => {
  const [recovery, setRecovery] = React.useState<{ token: string; status: 'pending' | 'failed' } | null>(null);
  // Set synchronously, so a re-run of the effect before the next render (StrictMode, or two
  // renders in a row) cannot start a second `/me` for the same token.
  const attemptedFor = React.useRef<string | null>(null);

  const isReady = !!authContext?.isReady;
  const hasSessionInState = !!authContext?.jwt;
  const logoutRedirect = !!authContext?.logoutRedirect;
  const initializeAuthentication = authContext?.initializeAuthentication;

  // Read on every render: this is exactly the divergence between state and storage, and a copy of
  // either would hide it.
  const storedToken = typeof window === 'undefined' ? null : readStoredToken();
  const storedTokenValid = isValidToken(storedToken);
  const diverged = isPrivatePath(path) && isReady && !hasSessionInState && !logoutRedirect && storedTokenValid;

  React.useEffect(() => {
    if (!diverged || !storedToken || !initializeAuthentication) return;
    if (attemptedFor.current === storedToken) return;
    attemptedFor.current = storedToken;

    setRecovery({ token: storedToken, status: 'pending' });
    trackSessionRecovery({ outcome: 'started', path });

    let timer: ReturnType<typeof setTimeout> | undefined;
    const giveUp = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, SESSION_RECOVERY_TIMEOUT_MS);
    });
    Promise.race([initializeAuthentication(storedToken).catch(() => undefined), giveUp]).then(() => {
      clearTimeout(timer);
      // Settled: whether the session came back is read from the state `initialize()` just
      // dispatched, on the next render, not guessed here.
      setRecovery((current) => (current?.token === storedToken ? { token: storedToken, status: 'failed' } : current));
    });
    // `path` is reported, not a reason to re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diverged, storedToken, initializeAuthentication]);

  // Back in state: forget the attempt, so a later episode with the same token gets its own.
  React.useEffect(() => {
    if (!hasSessionInState || !recovery) return;
    trackSessionRecovery({ outcome: 'recovered', path });
    attemptedFor.current = null;
    setRecovery(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSessionInState, recovery]);

  // Failed for the token in storage, or for one the re-check itself threw away: a 401 during the
  // re-check clears storage, and that is a failed recovery, not a visitor who never had a token.
  // `initialize()` clears storage and dispatches in the same tick, before the promise above settles,
  // so an attempt still marked pending with storage already empty is that 401.
  const recoveryFailed =
    !!recovery &&
    !hasSessionInState &&
    ((recovery.status === 'failed' && recovery.token === storedToken) || !storedToken);

  React.useEffect(() => {
    if (recoveryFailed) trackSessionRecovery({ outcome: 'failed', path });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveryFailed]);

  return {
    /** Hold the page on the loader: the session is being re-checked, or is about to be. */
    recovering: diverged && !recoveryFailed,
    redirectReason: (recoveryFailed
      ? 'recovery_failed'
      : storedToken
      ? 'invalid_token'
      : 'no_token') as AuthGuardRedirectReason,
    tokenPresent: !!storedToken,
    tokenValid: storedTokenValid,
  };
};

/** Says, once per redirect, why the guard is sending a visitor to `/login`. */
const LoginRedirect = ({ to, report }: { to: string; report: Parameters<typeof trackAuthGuardRedirect>[0] }) => {
  React.useEffect(() => {
    trackAuthGuardRedirect(report);
    // One report per destination, like the navigation itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);
  return <Redirect to={to} />;
};

export const AuthGuard = ({ children, loader }: AuthGuardProps): React.ReactElement<any> => {
  const router = useRouter();
  const authContext = useAuthContext();
  const isAdminEmployer = useIsEmployerAnAdmin();
  const isEmployerInfoLoading = useEmployerUserInformationLoadingState();
  const recovery = useSessionRecovery(authContext, router.asPath);

  if (authContext) {
    const { user: userData, isReady, jwt, isAuthenticated, logoutRedirect } = authContext;
    const { query, asPath } = router;
    const currentPath = router.asPath;
    // Same source the login screen reads: the magic-link token is taken out of the address
    // bar before Next parses it, so `router.query` no longer carries it. Without this the
    // guard would send an already-signed-in visitor away from /login, and a magic link for
    // a different account would never get the chance to switch user.
    const token = consumeLoginTokenFromUrl() || router?.query?.['token']?.toString() || '';

    // Check if the URL includes a "?"
    const indexOfQuestionMark = currentPath.indexOf('?');

    // Determine the base path based on whether there's a query string
    const basepath = indexOfQuestionMark >= 0 ? currentPath.slice(0, indexOfQuestionMark) : currentPath;
    let queryParams = '';
    if (query) {
      queryParams = Object.keys(query)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key] as string)}`)
        .join('&');
    }
    const currentUrl = `${basepath}${queryParams ? `?${queryParams}` : ''}`;
    const isPrivate = isPrivatePath(basepath);
    const isEmployer = !!userData && (userData.type === 'employer' || userData?.type === 'employer_contact');

    if (logoutRedirect && !isAuthenticated) {
      return <Redirect to={isPrivate ? '/' : currentUrl} refresh={true} />;
    }

    if ((!isReady || (isEmployer && isEmployerInfoLoading)) && isPrivate)
      return <ScreenSpinnerWrapper>{loader}</ScreenSpinnerWrapper>;

    // check if private route
    if (isPrivate) {
      // private routes
      if (!jwt && isReady && !logoutRedirect) {
        if (recovery.recovering) return <ScreenSpinnerWrapper>{loader}</ScreenSpinnerWrapper>;
        return (
          <LoginRedirect
            to={`${redirectLocation}?redirect_url=${currentUrl}`}
            report={{
              reason: recovery.redirectReason,
              isReady,
              isAuthenticated,
              tokenPresent: recovery.tokenPresent,
              tokenValid: recovery.tokenValid,
              path: basepath,
            }}
          />
        );
      }
      if (jwt) {
        if (asPath.startsWith('/worker/') || asPath.startsWith('/profilo') || asPath.startsWith('/candidature')) {
          if (userData && (userData.type === 'employer' || userData?.type === 'employer_contact'))
            return <Redirect to="/v2/employer/homepage" />;
          else if (userData && userData.type === 'ambassador') return <Redirect to="/challenge-wom/posizioni" />;
          else if (userData && userData.type === 'lead') return <Redirect to="/candidati" />;
        }
        if (asPath.startsWith('/employer/') || asPath.startsWith('/v2/employer/')) {
          if (userData && userData.type === 'worker') return <Redirect to="/profilo" />;
          else if (userData && userData.type === 'ambassador') return <Redirect to="/challenge-wom/posizioni" />;
          else if (userData && userData.type === 'lead') return <Redirect to="/" />;
          else if (userData && asPath.includes('payments-and-invoices') && !isAdminEmployer) return <Redirect to="/" />;
          else return <>{children}</>;
        }
        // if (asPath.startsWith('/profilo') && userData && userData.type === 'lead') return <Redirect to="/candidati" />;
        return <>{children}</>;
      }
    } else {
      // An employer who lands on the public homepage goes to their dashboard. That is not a
      // login arrival, so no destination is involved.
      if (asPath === '/' && isEmployer) return <Redirect to="/v2/employer/homepage" />;

      // A bare `/login` **is** a login arrival — the login screen has just taken the spent
      // token out of the address bar, and on a link with no destination it leaves nothing
      // behind. This used to send every employer to the dashboard before anything looked for a
      // destination, which is the same defect as the ladder below and fired one branch earlier.
      if (asPath === '/login' && jwt && isReady && isAuthenticated && !token)
        return <Redirect to={destinationAfterLogin(userData?.type, query)} />;
    }

    // public routes
    if (asPath.startsWith('/reset-password') && jwt && isReady) return <Redirect to="/profilo" />;
    if (asPath.startsWith('/login') || asPath.startsWith('/signup')) {
      if (jwt && isReady && isAuthenticated && !token) {
        return <Redirect to={destinationAfterLogin(userData?.type, query)} />;
      }
    }

    return <>{children}</>;
  }
  return <></>;
};

const ScreenSpinnerWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={`w-screen h-screen flex items-center justify-center ${className || ''}`} {...props} />
);

/** Where each kind of user ends up when the link named nowhere in particular. */
const homeFor = (userType: string | undefined): string => {
  if (userType === 'employer' || userType === 'employer_contact') return '/v2/employer/homepage';
  if (userType === 'worker') return '/profilo';
  if (userType === 'ambassador') return '/challenge-wom/posizioni';
  return '/';
};

/**
 * Where somebody who is already signed in and sitting on `/login` should go.
 *
 * **The destination is checked before the user type, and that is the change.** The ladder used
 * to read "if employer, go to the employer homepage; else if there is a destination, go
 * there", so for an `employer` or `employer_contact` the destination branch was unreachable —
 * the backend computed it, the e-mail carried it, and the guard ignored it. Four emitters were
 * written by someone who believed otherwise: the recommended-candidates digest, the
 * applications-without-feedback mail, the referral invite and the spontaneous-application
 * notification all name a page and all landed on the homepage instead. That has been true
 * since 28 March 2025 and was never a decision, just the order two commits happened to land in.
 *
 * Sending a user type somewhere it may not go is not this function's problem to solve, and
 * deliberately so: the private-route branch above already bounces a worker off `/employer/*`
 * and an employer off `/profilo`, so an impossible destination costs one extra hop and needs no
 * table here that could go stale as routes are added.
 *
 * `redirect_url` and `redirectTo` are the same thing under two names — the second is what the
 * job-favourite modal writes — so both are read, and **both go through `internalPath`**. The
 * reading side used to push whatever the query held, which meant the sanitising done on the
 * write side guarded the pipe and not the tap: a pasted or crafted `/login?redirect_url=…`
 * opened by somebody already signed in bypassed it entirely.
 */
const destinationAfterLogin = (
  userType: string | undefined,
  query: Record<string, string | string[] | undefined>
): string => {
  const home = homeFor(userType);
  const { path } = resolveAndReportLoginDestination(undefined, 'auth_guard', home);

  return path ?? internalPath(query?.['redirectTo']) ?? home;
};
